import { Box, useTheme } from '@mui/material';

import type { Layout, LayoutGridKey, LayoutWithPosition } from './types';

interface LayoutPreviewGeneratorProps<T extends string> {
  layout: Layout<T>[];
  breakpoint?: LayoutGridKey;
  height?: number | string;
  containerPadding?: number;
  gap?: number;
  maxRows?: number;
}

/**
 * Automatically generates a skeleton preview of a layout based on its structure.
 * Uses the same positioning algorithm as LayoutGrid to ensure accurate previews.
 *
 * @example
 * ```tsx
 * <LayoutPreviewGenerator
 *   layout={layouts.lg}
 *   height={180}
 *   gap={4}
 * />
 * ```
 */
export function LayoutPreviewGenerator<T extends string>({
  layout,
  height = 180,
  containerPadding = 2,
  gap = 4,
  maxRows,
}: LayoutPreviewGeneratorProps<T>) {
  const theme = useTheme();

  // Calculate X and Y positions for all widgets (same algorithm as LayoutGrid)
  const calculateLayoutPositions = (
    items: Layout<T>[]
  ): LayoutWithPosition<T>[] => {
    if (!items || items.length === 0) return [];

    const grid: boolean[][] = [];

    const canFitAt = (x: number, y: number, w: number, h: number): boolean => {
      if (x + w > 12) return false;
      for (let row = y; row < y + h; row++) {
        if (!grid[row]) continue;
        for (let col = x; col < x + w; col++) {
          if (grid[row][col]) return false;
        }
      }
      return true;
    };

    const occupy = (x: number, y: number, w: number, h: number): void => {
      for (let row = y; row < y + h; row++) {
        if (!grid[row]) {
          grid[row] = new Array(12).fill(false);
        }
        for (let col = x; col < x + w; col++) {
          grid[row][col] = true;
        }
      }
    };

    return items.map((item) => {
      let foundX = 0;
      let foundY = 0;
      let found = false;

      for (let y = 0; !found; y++) {
        for (let x = 0; x <= 12 - item.w; x++) {
          if (canFitAt(x, y, item.w, item.h)) {
            foundX = x;
            foundY = y;
            found = true;
            break;
          }
        }
      }

      occupy(foundX, foundY, item.w, item.h);

      return {
        ...item,
        x: foundX,
        y: foundY,
      };
    });
  };

  const positionedLayout = calculateLayoutPositions(layout);

  // Calculate total rows needed
  const totalRows = positionedLayout.reduce((max, item) => {
    const itemEnd = item.y + item.h;
    return itemEnd > max ? itemEnd : max;
  }, 0);

  // Use maxRows if provided, otherwise use calculated totalRows
  const displayRows = maxRows ?? totalRows;

  // Calculate row height to fit within container
  const rowHeight = typeof height === 'number' ? height / displayRows : 20;

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(12, 1fr)',
        gridTemplateRows: `repeat(${displayRows}, ${rowHeight}px)`,
        gap: `${gap}px`,
        p: containerPadding,
        bgcolor: theme.palette.background.default,
        borderRadius: 1,
        height: height,
        overflow: 'hidden',
      }}
    >
      {positionedLayout.map((item, index) => (
        <Box
          key={item.i || index}
          sx={{
            gridColumn: `${item.x + 1} / ${item.x + item.w + 1}`,
            gridRow: `${item.y + 1} / ${item.y + item.h + 1}`,
            bgcolor: theme.palette.action.hover,
            borderRadius: 0.5,
            transition: 'background-color 0.2s',
            '&:hover': {
              bgcolor: theme.palette.action.selected,
            },
          }}
        />
      ))}
    </Box>
  );
}

/**
 * Hook to generate layout preview component from layout definition.
 * Useful for creating preview options in layout selectors.
 *
 * @example
 * ```tsx
 * const preview = useLayoutPreview(layouts.lg, { height: 180 });
 * ```
 */
export function useLayoutPreview<T extends string>(
  layout: Layout<T>[],
  options?: Omit<LayoutPreviewGeneratorProps<T>, 'layout'>
) {
  return <LayoutPreviewGenerator layout={layout} {...options} />;
}

/**
 * Generate multiple layout previews for different breakpoints.
 * Automatically selects the largest available breakpoint for preview.
 *
 * @example
 * ```tsx
 * const preview = generateLayoutPreview({
 *   layouts: { lg: [...], md: [...], sm: [...] },
 *   preferredBreakpoint: 'lg'
 * });
 * ```
 */
export function generateLayoutPreview<T extends string>(config: {
  layouts: Partial<Record<LayoutGridKey, Layout<T>[]>>;
  preferredBreakpoint?: LayoutGridKey;
  height?: number | string;
  containerPadding?: number;
  gap?: number;
  maxRows?: number;
}) {
  const {
    layouts,
    preferredBreakpoint = 'lg',
    height,
    containerPadding,
    gap,
    maxRows,
  } = config;

  // Try preferred breakpoint first, then fallback to largest available
  const breakpointOrder: LayoutGridKey[] = [
    preferredBreakpoint,
    'lg',
    'md',
    'sm',
    'xs',
    'xxs',
  ];

  let selectedLayout: Layout<T>[] | undefined;
  for (const bp of breakpointOrder) {
    if (layouts[bp] && layouts[bp]!.length > 0) {
      selectedLayout = layouts[bp];
      break;
    }
  }

  if (!selectedLayout || selectedLayout.length === 0) {
    return null;
  }

  return (
    <LayoutPreviewGenerator
      layout={selectedLayout}
      height={height}
      containerPadding={containerPadding}
      gap={gap}
      maxRows={maxRows}
    />
  );
}

/**
 * Generate layout option with automatic preview generation.
 * Use this instead of manually creating preview components.
 *
 * @example
 * ```tsx
 * const options = [
 *   createLayoutOption({
 *     template: 'executive',
 *     name: 'Executive View',
 *     description: 'Minimalist layout...',
 *     layouts: executiveLayouts,
 *   }),
 *   createLayoutOption({
 *     template: 'operational',
 *     name: 'Operational View',
 *     description: 'Comprehensive layout...',
 *     layouts: operationalLayouts,
 *   }),
 * ];
 * ```
 */
export function createLayoutOption<
  T extends string,
  TTemplate extends string,
>(config: {
  template: TTemplate;
  name: string;
  description: string;
  layouts: Partial<Record<LayoutGridKey, Layout<T>[]>>;
  previewBreakpoint?: LayoutGridKey;
  previewHeight?: number;
  previewGap?: number;
  previewMaxRows?: number;
}): {
  template: TTemplate;
  name: string;
  description: string;
  preview: JSX.Element;
} {
  const {
    template,
    name,
    description,
    layouts,
    previewBreakpoint = 'lg',
    previewHeight = 250,
    previewGap = 6,
    previewMaxRows,
  } = config;

  const preview = generateLayoutPreview({
    layouts,
    preferredBreakpoint: previewBreakpoint,
    height: previewHeight,
    gap: previewGap,
    maxRows: previewMaxRows,
  });

  if (!preview) {
    throw new Error(
      `Failed to generate preview for layout template "${template}". No layout definitions found.`
    );
  }

  return {
    template,
    name,
    description,
    preview,
  };
}
